"use strict";

exports.__esModule = true;
exports.UI_PREFERENCES_VERSION = exports.EMPTY_UI_PREFERENCES = void 0;
exports.applyUiPreferences = applyUiPreferences;
exports.describeEnvironmentUi = describeEnvironmentUi;
exports.isDefaultUiPreferences = isDefaultUiPreferences;
exports.normalizeUiPreferences = normalizeUiPreferences;
exports.uiPreferencesEnabled = uiPreferencesEnabled;
var _featureFlags = require("../../featureFlags");
var _resolveUiDesign = require("../Theme/UiDesign/resolveUiDesign");
var UI_PREFERENCES_VERSION = exports.UI_PREFERENCES_VERSION = 1;

/**
 * Whether this deployment lets users choose their interface at all. Without
 * ENABLE_UI_PREFERENCES the switch and the dialog are not offered, a ?ui=
 * link does nothing and a record saved earlier is ignored: the environment's
 * flags alone decide what renders.
 */
function uiPreferencesEnabled(featureFlags) {
  return Array.isArray(featureFlags) && featureFlags.includes(_featureFlags.ENABLE_UI_PREFERENCES);
}

/**
 * A user's interface choices. `modern` and each screen are tri-state:
 * true / false override the environment's flag, null (or absent) follows it.
 */
var EMPTY_UI_PREFERENCES = exports.EMPTY_UI_PREFERENCES = Object.freeze({
  version: UI_PREFERENCES_VERSION,
  modern: null,
  screens: Object.freeze({})
});
var triState = function triState(value) {
  return value === true || value === false ? value : null;
};

/**
 * A clean copy of whatever was stored (browser or service): unknown screens
 * and non-boolean values are dropped, so a stale or hand-edited record can
 * never put the app in an odd state.
 */
function normalizeUiPreferences(raw) {
  if (!raw || typeof raw !== "object") {
    return Object.assign({}, EMPTY_UI_PREFERENCES, {
      screens: {}
    });
  }
  var screens = {};
  Object.keys(_resolveUiDesign.UI_SCREENS).forEach(function (screen) {
    var value = raw.screens ? triState(raw.screens[screen]) : null;
    if (value !== null) {
      screens[screen] = value;
    }
  });
  return {
    version: UI_PREFERENCES_VERSION,
    modern: triState(raw.modern),
    screens: screens
  };
}
function isDefaultUiPreferences(preferences) {
  var clean = normalizeUiPreferences(preferences);
  return clean.modern === null && Object.keys(clean.screens).length === 0;
}

/**
 * The environment's feature flags with the user's choices applied: a true
 * adds the flag, a false removes it, null leaves the environment's value.
 * Where the environment does not allow preferences (uiPreferencesEnabled)
 * the choices are ignored and the flags come back as they are. The result
 * feeds resolveUiDesign unchanged, so the flags stay the single vocabulary
 * for "which UI".
 */
function applyUiPreferences(featureFlags, preferences) {
  var flags = new Set(Array.isArray(featureFlags) ? featureFlags : []);
  if (!uiPreferencesEnabled(featureFlags)) {
    return Array.from(flags);
  }
  var clean = normalizeUiPreferences(preferences);
  var apply = function apply(flag, value) {
    if (value === true) {
      flags.add(flag);
    } else if (value === false) {
      flags["delete"](flag);
    }
  };
  apply(_featureFlags.ENABLE_MODERN_UI, clean.modern);
  Object.keys(_resolveUiDesign.UI_SCREENS).forEach(function (screen) {
    return apply(_resolveUiDesign.UI_SCREENS[screen], clean.screens[screen]);
  });
  return Array.from(flags);
}

/**
 * What the environment alone would show, for the dialog's "default" hints.
 */
function describeEnvironmentUi(featureFlags) {
  var has = function has(flag) {
    return Array.isArray(featureFlags) && featureFlags.includes(flag);
  };
  var screens = {};
  Object.keys(_resolveUiDesign.UI_SCREENS).forEach(function (screen) {
    screens[screen] = has(_resolveUiDesign.UI_SCREENS[screen]);
  });
  return {
    modern: has(_featureFlags.ENABLE_MODERN_UI),
    screens: screens
  };
}